/**
 * Authentication Query Hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type {
  LoginCredentials,
  LoginResponse,
  RegisterData,
  RegisterResponse,
  User,
} from '@shared/types'
import { apiClient } from '../client'
import { API_ENDPOINTS } from '../endpoints'
import { tokenStorage } from '@shared/lib/storage'

// ============================================================================
// Query Keys
// ============================================================================

export const authKeys = {
  all: ['auth'] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
  validate: (token: string) => [...authKeys.all, 'validate', token] as const,
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get current user profile
 * Uses httpOnly cookie for authentication
 */
export function useProfile(enabled = true) {
  return useQuery({
    queryKey: authKeys.profile(),
    queryFn: () => apiClient.get<User>(API_ENDPOINTS.auth.profile),
    enabled,
    retry: false, // Don't retry 401 errors
  })
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Login mutation
 */
export function useLogin() {
  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      apiClient.post<LoginResponse>(API_ENDPOINTS.auth.login, credentials),
    onSuccess: (data) => {
      tokenStorage.setToken(data.token)
    },
  })
}

/**
 * Register mutation
 */
export function useRegister() {
  return useMutation({
    mutationFn: (data: RegisterData) =>
      apiClient.post<RegisterResponse>(API_ENDPOINTS.auth.register, data),
    onSuccess: (data) => {
      tokenStorage.setToken(data.token)
    },
  })
}

/**
 * Logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Clear httpOnly cookie on backend
      await apiClient.post(API_ENDPOINTS.auth.logout)
    },
    onSuccess: () => {
      // Clear JWT token from localStorage
      tokenStorage.clearToken()

      // Clear all queries
      queryClient.clear()
    },
  })
}

/**
 * Validate account mutation
 */
export function useValidateAccount() {
  return useMutation({
    mutationFn: (token: string) =>
      apiClient.post(API_ENDPOINTS.auth.validate, { token }),
  })
}

/**
 * Password recovery mutation
 */
export function usePasswordRecovery() {
  return useMutation({
    mutationFn: (data: { email: string }) =>
      apiClient.post(API_ENDPOINTS.auth.passwordRecovery, data),
  })
}

/**
 * Password reset mutation
 */
export function usePasswordReset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { token: string; newPassword: string }) =>
      apiClient.post<{ token: string }>(API_ENDPOINTS.auth.passwordReset, data),
    onSuccess: (data) => {
      tokenStorage.setToken(data.token)
      void queryClient.invalidateQueries({ queryKey: authKeys.profile() })
    },
  })
}

/**
 * Get account validation status
 *
 * Automatically invalidates the user profile cache when validation status
 * returns true, ensuring the profile data stays synchronized.
 *
 * @param enabled - Whether to enable automatic fetching (default: false)
 * @returns Query result with validAccount boolean
 *
 * @example
 * // Manual polling (recommended for validation flow)
 * const { refetch } = useValidationStatus(false)
 * const { data: isValidated } = await refetch()
 *
 * @example
 * // Auto-fetch on mount
 * const { data: isValidated } = useValidationStatus(true)
 *
 * @note This hook automatically syncs the user profile when validation
 * status changes to true. The profile cache is invalidated, triggering
 * a refetch of the full user data.
 */
export function useValidationStatus(enabled = false) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: [...authKeys.all, 'validation-status'] as const,
    queryFn: async () => {
      const response = await apiClient.get<{ validAccount: boolean }>(
        API_ENDPOINTS.users.validationStatus
      )
      return response.validAccount
    },
    enabled,
    retry: false, // Don't retry 401 errors
    refetchOnWindowFocus: false, // Don't auto-refetch on window focus
    staleTime: 0, // Always consider stale for polling scenarios
  })

  // Automatically sync user profile when validation status becomes true
  useEffect(() => {
    if (query.isSuccess && query.data === true) {
      // Invalidate profile cache to trigger refetch with updated validation status
      void queryClient.invalidateQueries({ queryKey: authKeys.profile() })
      // Also invalidate user profile cache (used by ProfilePage)
      void queryClient.invalidateQueries({ queryKey: ['userProfile'] })
    }
  }, [query.isSuccess, query.data, queryClient])

  return query
}
